import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Card } from '../../../../../CommonComponents/OPL/Card/Card';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { Textarea } from '../../../../../CommonComponents/OPL/Textarea/Textarea';
import { Chart } from '../Chart/Chart';
import { DraggableItem } from '../DraggableItem/DraggableItem';
import { Droppable } from '../Droppable/Droppable';
import { ClassificationDTO } from '../../api/useAssetBucketQuery';
import { HelperText } from '../../../../../CommonComponents/OPL/HelperText/HelperText';
import { ScrollHereOnce } from '../../../../../CommonComponents/ScrollHereOnce';

interface Props {
  classifications: ClassificationDTO[];
  bucketIndex: number;
  droppableId: string;
  removeItem: ({
    classification,
    bucketIndex,
  }: {
    classification: ClassificationDTO;
    bucketIndex: number;
  }) => void;
}

export const Bucket: React.FC<Props> = ({
  bucketIndex,
  classifications,
  removeItem,
  droppableId,
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const noAssetTypesErrorMessage = errors?.buckets?.[bucketIndex]?.classifications?.message;

  return (
    <Card display="flex" flexDirection="column" flex={1} p="24">
      <Input
        {...register(`buckets.${bucketIndex}.title`)}
        label="Bucket Name"
        type="text"
        mb="16"
        formVariant={errors?.buckets?.[bucketIndex]?.title?.message ? 'error' : undefined}
        helperText={errors?.buckets?.[bucketIndex]?.title?.message || 'Max. 40 characters'}
        maxLength={40}
      />

      <Textarea
        {...register(`buckets.${bucketIndex}.description`)}
        maxLength={250}
        rows={4}
        label="Description"
        mt="16"
        formVariant={errors?.buckets?.[bucketIndex]?.description?.message ? 'error' : undefined}
        helperText={errors?.buckets?.[bucketIndex]?.description?.message || 'Max. 250 characters'}
      />

      <Chart />

      <Droppable id={droppableId} bucketIndex={bucketIndex} isError={noAssetTypesErrorMessage}>
        {classifications.map(classification => (
          <Box key={classification.classificationId} as="li" mb="8">
            <DraggableItem
              {...classification}
              id={classification.classificationId}
              bucketIndex={bucketIndex}
              remove={() => removeItem({ classification, bucketIndex })}
            />
          </Box>
        ))}
      </Droppable>

      {noAssetTypesErrorMessage && (
        <>
          <ScrollHereOnce
            key={noAssetTypesErrorMessage}
            options={{
              block: 'center',
            }}
          />

          <HelperText variant="error" mt="4">
            {noAssetTypesErrorMessage}
          </HelperText>
        </>
      )}
    </Card>
  );
};
